// Small shared UI parts.
import { useState, type ReactNode } from "react";
import { formatTime } from "../lib/format";
import { ApiError } from "../api/client";

/** Converts any error to a message for the user. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const ref = err.requestId ? ` (request ${err.requestId})` : "";
    // A name that is still taken by an object pending deletion: say until when it can be restored.
    const pending = err.pendingDeletion
      ? ` It is in "Pending deletion" and can be restored until ${formatTime(err.pendingDeletion.purge_after)}.`
      : "";
    return `${err.message}${ref}${pending}`;
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong.";
}

/** Shows an error, or nothing. */
export function ErrorBanner({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p className="banner error" role="alert">
      {errorMessage(error)}
    </p>
  );
}

/** Button that copies text to the clipboard. */
export function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="secondary"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setDone(true);
          setTimeout(() => setDone(false), 2000);
        } catch {
          setDone(false);
        }
      }}
    >
      {done ? "Copied" : label}
    </button>
  );
}

/** Code block with a copy button. */
export function CodeBlock({ code, title }: { code: string; title: string }) {
  return (
    <figure className="code">
      <figcaption>
        <span>{title}</span>
        <CopyButton text={code} />
      </figcaption>
      <pre>
        <code>{code}</code>
      </pre>
    </figure>
  );
}

/** Page section with a heading. */
export function Section({
  title,
  children,
  actions,
}: {
  title: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <section className="card">
      <header className="card-head">
        <h2>{title}</h2>
        {actions}
      </header>
      {children}
    </section>
  );
}

/** Runs an async action from a form or button, with busy state and error. */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  async function run(fn: () => Promise<void>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await fn();
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      setBusy(false);
    }
  }
  return { busy, error, run, setError };
}
